import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { get, set, del, createStore } from 'idb-keyval';
import { isServerRender, noopStorage } from './ssr-safe-storage';

export interface PersisterStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

function webStorage(): PersisterStorage {
  // Dedicated store so we don't collide with anything else the app or a
  // third party might write to the default `keyval-store`.
  const store = createStore('kiips-rq-cache', 'entries');
  return {
    async getItem(key) {
      const v = await get<string>(key, store);
      return v ?? null;
    },
    async setItem(key, value) {
      await set(key, value, store);
    },
    async removeItem(key) {
      await del(key, store);
    },
  };
}

function asyncStorageWrapper(): PersisterStorage {
  return {
    getItem: (key) => AsyncStorage.getItem(key),
    setItem: (key, value) => AsyncStorage.setItem(key, value),
    removeItem: (key) => AsyncStorage.removeItem(key),
  };
}

// On web, route persistence through IndexedDB so we're not bounded by the
// ~5MB localStorage quota and writes don't block the main thread. On native
// we keep AsyncStorage — its MMKV/SQLite backends already meet the same
// goals.
export const idbPersisterStorage: PersisterStorage = isServerRender
  ? noopStorage
  : Platform.OS === 'web' && typeof indexedDB !== 'undefined'
    ? webStorage()
    : asyncStorageWrapper();
